import type { NodeApi } from "@hogartrabe/react-arborist-vrh";
import type { Project } from "../projectStore";
import {
  calculateNewNodePosition,
  calculatePartiallyVisible,
  updateAncestorsActiveStatus,
  updateSiblingsAfterInsert,
} from "./utils";
import { generateFolderName } from "./utils/generateFolderName";
import type { Node } from "interfaces/schemas";
import { NodeSchema, type Folder } from "interfaces/schemas";
import * as treeUtils from "utils/treeUtils";
import type { NodeRequestItem } from "./utils/createNodeInput";
import { createNodesInput } from "./utils/createNodeInput";
import { NodeKinds } from "interfaces/enums/nodeKinds";
import { processNodes } from "./utils/mutations";

export const pasteNode = (
  node: NodeApi<Node> | null,
  parent: NodeApi<Node> | null,
  tree: Node[],
  project: Project,
  language: string,
  price: number | null,
  callback?: (node: Node) => void,
) => {
  if (!node) {
    throw new Error("No node to paste");
  }
  if (!parent) {
    throw new Error("No parent node to paste into");
  }
  if (!project?.id) {
    throw new Error("Project ID is missing. Cannot paste node.");
  }

  if (!Array.isArray(parent.data.nodes)) {
    parent.data.nodes = [];
  }

  const folderData: Partial<Folder> = {};
  if (treeUtils.isAnyFolder(node.data)) {
    const newFolderName = generateFolderName(
      node.data.folderName,
      parent.data.nodes,
      false,
    );
    folderData.folderName = newFolderName;
  }

  const newNode = createNodesInput({
    data: {
      ...node.data,
      ...(treeUtils.isAnyFolder(node.data)
        ? { folderName: folderData.folderName }
        : {}),
      parentId: treeUtils.validateNodeId(parent.id, project.id),
      position: calculateNewNodePosition(node.data, parent.data),
    },
    callback: (nodeData) => {
      if (treeUtils.isWizard(nodeData) || treeUtils.isTroxNetcom(nodeData)) {
        if (treeUtils.isWizardCommissioningCost(nodeData)) {
          // After copying Wizard commissioning cost turns into a Custom product (comment under ticket 73160)
          const parsedNodeData = NodeSchema.parse({
            ...nodeData,
            kind: NodeKinds.CUSTOM_PRODUCT,
            productName: nodeData.productName ?? "Commissioning",
            price,
          });
          return parsedNodeData;
        }
        const isAnyProduct = treeUtils.isAnyProduct(nodeData);
        const isTroxNetcom = treeUtils.isTroxNetcom(nodeData);
        let kind;
        if (isAnyProduct) {
          kind = treeUtils.isAnyAccessory(nodeData)
            ? NodeKinds.PRODUCT_ACCESSORY
            : NodeKinds.PRODUCT;
        } else {
          kind = NodeKinds.FOLDER;
        }
        if (isTroxNetcom && isAnyProduct) {
          kind = NodeKinds.CUSTOM_PRODUCT;
        }
        // revalidate the node data with the correct kind to ensure it meets the schema requirements for the new kind
        const parsedNodeData = NodeSchema.safeParse({
          ...nodeData,
          kind,
        });
        if (!parsedNodeData.success) {
          throw new Error(
            `Failed to validate node data: ${parsedNodeData.error.message}`,
          );
        }
        return parsedNodeData.data;
      }
      return nodeData;
    },
  });

  if (newNode.error) {
    throw new Error(newNode.error.message);
  }

  if (!newNode.subTree) {
    throw new Error("failed to create node");
  }

  parent.data.nodes.splice(newNode.subTree.position, 0, newNode.subTree);

  const newAncestorsToUpdate: NodeRequestItem[] = [];

  const newParentPartiallyVisible = calculatePartiallyVisible(parent.data);

  updateAncestorsActiveStatus(parent, newAncestorsToUpdate, project.id, {
    prev: {
      active: newNode.subTree.active,
      partiallyVisible: newParentPartiallyVisible,
    },
  });

  const nodesToUpdate = new Map();

  updateSiblingsAfterInsert(
    parent.data.nodes,
    newNode.subTree.position,
    nodesToUpdate,
  );

  const updateNodes = Array.from(nodesToUpdate.values());
  processNodes(
    [
      {
        action: "CREATE",
        nodes: newNode.nodesToRequest ?? [],
      },
      {
        action: "UPDATE",
        nodes: [...updateNodes, ...newAncestorsToUpdate],
      },
    ],
    project,
    language,
  );
  callback?.(newNode.subTree);

  return [...tree];
};
